import type {
  TeacherInviteCreateResponse,
  TeacherInviteValidateResponse,
  TeacherLoginResponse,
  TeacherProfileResponse,
} from '../types/backend';
import { ApiError, UNAVAILABLE_MESSAGE, apiErrorFromResponse, fetchJson, resolveApiUrl } from './http';
import {
  TEACHER_SESSION_HEADER,
  clearTeacherSession,
  readTeacherSession,
  writeTeacherSession,
  type TeacherSession,
} from '../lib/teacherSession';
import { clearAdminToken } from '../lib/adminToken';

const WRONG_CREDENTIALS_MESSAGE = 'Forkert brugernavn eller adgangskode.';
const ADMIN_DISABLED_MESSAGE = 'Læreradgang er ikke aktiveret på serveren.';
const PASSWORD_CHANGE_INVALID_MESSAGE =
  'Forkert adgangskode, eller den nye adgangskode er ugyldig (8–128 tegn).';

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError')
    || (error instanceof Error && error.name === 'AbortError')
  );
}

function sessionFromLoginResponse(data: TeacherLoginResponse): TeacherSession {
  return {
    token: data.token,
    expiresAt: data.expires_at,
  };
}

export async function teacherLogin(email: string, password: string): Promise<TeacherLoginResponse> {
  try {
    const data = await fetchJson<TeacherLoginResponse>('/chat/admin/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    writeTeacherSession(sessionFromLoginResponse(data));
    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.httpStatus === 401) {
        throw new ApiError({
          httpStatus: 401,
          code: null,
          userMessage: WRONG_CREDENTIALS_MESSAGE,
        });
      }
      if (error.httpStatus === 404) {
        throw new ApiError({
          httpStatus: 404,
          code: null,
          userMessage: ADMIN_DISABLED_MESSAGE,
        });
      }
    }
    throw error;
  }
}

export async function teacherLogout(): Promise<void> {
  const token = readTeacherSession()?.token?.trim() ?? '';
  clearTeacherSession();
  if (!token) {
    return;
  }
  try {
    await fetchJson<void>('/chat/admin/auth/logout', {
      method: 'POST',
      teacher: true,
      headers: { [TEACHER_SESSION_HEADER]: token },
    });
  } catch {
    /* Local session is already cleared so the UI can leave /admin immediately. */
  }
}

export async function validateTeacherSession(signal?: AbortSignal): Promise<void> {
  await fetchJson<void>('/chat/admin/auth/session', {
    method: 'GET',
    teacher: true,
    signal,
  });
}

export async function getTeacherProfile(signal?: AbortSignal): Promise<TeacherProfileResponse> {
  return fetchJson<TeacherProfileResponse>('/chat/admin/auth/profile', {
    method: 'GET',
    teacher: true,
    signal,
  });
}

export async function patchTeacherProfile(
  patch: { displayName?: string | null },
  signal?: AbortSignal,
): Promise<TeacherProfileResponse> {
  const body: { display_name?: string | null } = {};
  if (patch.displayName !== undefined) {
    body.display_name = patch.displayName;
  }
  return fetchJson<TeacherProfileResponse>('/chat/admin/auth/profile', {
    method: 'PATCH',
    teacher: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
}

export async function changeTeacherPassword(
  currentPassword: string,
  newPassword: string,
  signal?: AbortSignal,
): Promise<void> {
  try {
    await fetchJson<void>('/chat/admin/auth/password', {
      method: 'POST',
      teacher: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
      signal,
    });
  } catch (error) {
    if (error instanceof ApiError && error.httpStatus === 422 && error.code === 'profile_metadata_invalid') {
      throw new ApiError({
        httpStatus: 422,
        code: 'profile_metadata_invalid',
        userMessage: PASSWORD_CHANGE_INVALID_MESSAGE,
      });
    }
    throw error;
  }
}

export async function validateTeacherInvite(token: string, signal?: AbortSignal): Promise<{ email: string }> {
  const data = await fetchJson<TeacherInviteValidateResponse>(
    '/chat/teacher/invite',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      signal,
    },
  );
  return { email: data.email };
}

export interface CompleteTeacherInviteInput {
  token: string;
  email: string;
  password: string;
  displayName: string;
  metadata?: Record<string, string | number | boolean | null>;
}

export async function completeTeacherInvite(
  input: CompleteTeacherInviteInput,
): Promise<TeacherLoginResponse> {
  const data = await fetchJson<TeacherLoginResponse>('/chat/teacher/invite/complete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: input.token,
      email: input.email,
      password: input.password,
      display_name: input.displayName,
      ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
    }),
  });
  clearAdminToken();
  writeTeacherSession(sessionFromLoginResponse(data));
  return data;
}

export async function mintTeacherInvite(email: string): Promise<TeacherInviteCreateResponse> {
  return fetchJson<TeacherInviteCreateResponse>('/chat/admin/teacher-invites', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
    admin: true,
  });
}

/** Raw session probe with explicit teacher token (login dialog validation). */
export async function validateTeacherSessionToken(token: string, signal?: AbortSignal): Promise<void> {
  let response: Response;
  try {
    response = await fetch(resolveApiUrl('/chat/admin/auth/session'), {
      method: 'GET',
      cache: 'no-store',
      signal,
      headers: {
        Accept: 'application/json',
        'X-Teacher-Session': token,
      },
    });
  } catch (error) {
    if (signal?.aborted || isAbortError(error)) {
      throw error instanceof Error ? error : new DOMException('Aborted', 'AbortError');
    }
    throw new ApiError({
      httpStatus: 0,
      code: null,
      userMessage: UNAVAILABLE_MESSAGE,
    });
  }

  if (response.status === 204) {
    return;
  }

  if (response.status === 401) {
    throw new ApiError({
      httpStatus: 401,
      code: null,
      userMessage: WRONG_CREDENTIALS_MESSAGE,
    });
  }

  if (response.status === 404) {
    throw new ApiError({
      httpStatus: 404,
      code: null,
      userMessage: ADMIN_DISABLED_MESSAGE,
    });
  }

  throw await apiErrorFromResponse(response);
}
